// Placeholder data for preview - matches templates_helper.rb placeholder_template_data
export const PLACEHOLDER_DATA = {
  'First Name': 'John',
  'Last Name': 'Smith',
  'Student ID': '123456',
  'Grade': '12',
  'Email': 'john@smith.com',
  'Date of Birth': '12/25/1995',
  'Teacher': 'Ms. Frizzle',
  'Teacher display name': 'Ms. Frizzle',
  'Shoob ID': '483R797248',
  'Shoob ID for Spring': '483R797248',
  'Shoob ID for Fall': '483R797248',
  'Shoob ID for Grad': '483R797248',
  'Grade with label': 'Grade: 12',
  'Last Name First Name': 'Smith, John',
  'First Name Last Name': 'John Smith',
  'Access Code for Fall': 'A1B2C3',
  'Access Code for Spring': 'A1B2C3',
  'Access Code for Grad': 'A1B2C3',
  'Access Code for Seniors': 'A1B2C3',
  'Sitting Name': 'sitting',
  'School Name': 'Franklin Elementary School',
  'Barcode': '12345678910',
  'Shoob ID for Fall Barcode': '483R797248',
  'Shoob ID for Spring Barcode': '483R797248',
  'Shoob ID for Grad Barcode': '483R797248',
  'Data1': 'Data1',
  'Data2': 'Data2',
  'Data3': 'Data3',
  'Data4': 'Data4',
  'Custom': 'Custom',
  'Time Period': 'First Trimester',
  'Principal': 'Ms. Frizzle',
  'Award Date': 'Award Date',
  'Character Trait': 'Character Trait',
  'Awarded For': 'Awarded For',
}

// Get placeholder text for a field based on its label/column name
export function getPlaceholderText(label) {
  if (!label) return null
  // Direct match
  if (PLACEHOLDER_DATA[label]) return PLACEHOLDER_DATA[label]
  // Try without case sensitivity
  const lowerLabel = label.toLowerCase()
  for (const [key, value] of Object.entries(PLACEHOLDER_DATA)) {
    if (key.toLowerCase() === lowerLabel) return value
  }
  return null
}

// Calculate canvas dimensions from template
export function getCanvasDimensions(scalingFactor, templateWidth, templateHeight) {
  const factor = scalingFactor || 1
  if (templateWidth && templateHeight) {
    return {
      width: templateWidth / factor,
      height: templateHeight / factor
    }
  }
  return { width: 800, height: 600 }
}

// Ratio to multiply the on-screen canvas back up to native template
// resolution when exporting a proof image. Must fall back to the same
// factor getCanvasDimensions used to shrink the template down (1, when no
// scaling_factor is set) -- otherwise a template with no scaling_factor
// (every template created via NewTemplatesController#create, which forces
// use_new_scaling: false) exports at 3x its already-native-resolution
// image_width/image_height instead of 1x, ballooning a print-size poster
// export to 9x the intended pixel area and timing out proof generation.
export function getExportRatio(pixelRatio, scalingFactor) {
  return pixelRatio || (scalingFactor || 1)
}

// Categories whose async full-res background render
// (TemplateFullResProofWorker) restores true print resolution after
// checkout -- MUST be kept in sync with
// TemplateProofRendering#large_format_custom_proof? on the Ruby side.
// 'poster_2' is labeled "Custom" in the admin category dropdown.
export const LARGE_FORMAT_CUSTOM_CATEGORIES = ['poster', 'poster_2', 'grad_folio']

export function isLargeFormatCustomCategory(category) {
  return LARGE_FORMAT_CUSTOM_CATEGORIES.includes(category)
}

// The proof exported here is a fast, on-screen approval preview for the
// large-format custom categories above -- not the final print file; they
// get their true print-resolution PDF from a server-side re-render after
// checkout (TemplateFullResProofWorker). A real 3600x5400 poster background
// at 1x already produces a ~19-megapixel export; compositing and uploading
// that from a customer's browser was slow/large enough to time out proof
// generation even after getExportRatio stopped tripling it further. Capping
// here is a no-op for small templates (ID cards etc. are already well
// under this) and bounds the export to a size that reliably finishes.
export const CUSTOMER_PROOF_MAX_DIM = 1600
export const CUSTOMER_PROOF_MAX_PIXELS = 2200000

// Safety-net cap for every OTHER category (browser canvas limits only).
// custom_template_enabled is a plain admin checkbox with no category
// restriction, so ANY physical-print category (sitting_cards, awards,
// envelopes, letters, yearbook covers, ...) can reach this same export path
// -- and none of them have an async full-res upgrade, so whatever the
// client exports here IS the permanent print file. Several of those
// categories' documented native sizes (Template::CATEGORIES/render_dpi,
// all authored at 300 DPI) already exceed CUSTOMER_PROOF_MAX_DIM/PIXELS on
// their own -- capping them the same way as the large-format categories
// would silently and permanently downsample their print file.
export const SAFETY_NET_MAX_DIM = 16384
export const SAFETY_NET_MAX_PIXELS = 268000000

// Scales ratio down (never up) so width*ratio/height*ratio stay within
// maxDim per side and maxPixels total, preserving aspect ratio. Returns a
// fractional ratio rather than flooring to an integer -- Konva's toDataURL
// accepts a fractional pixelRatio fine, and flooring a sub-1 scale (e.g.
// ratio 1 needing to shrink to 0.3x) would floor to 0 and produce a
// zero-size export.
export function capExportDimensions(width, height, ratio, maxDim, maxPixels) {
  const w = width * ratio
  const h = height * ratio
  if (w <= maxDim && h <= maxDim && (w * h) <= maxPixels) {
    return { ratio, width: w, height: h }
  }
  const scale = Math.min(maxDim / w, maxDim / h, Math.sqrt(maxPixels / (w * h)))
  const cappedRatio = ratio * scale
  return { ratio: cappedRatio, width: width * cappedRatio, height: height * cappedRatio }
}

// Calculate font size that fits text within width
export function calculateAutoFitFontSize(text, maxWidth, maxHeight, baseFontSize, minFontSize, fontFamily, wrap, letterSpacing = 0) {
  if (!text || maxWidth <= 0) return baseFontSize

  // Create a temporary canvas to measure text
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  let fontSize = baseFontSize
  // Add 15% safety margin to account for font rendering differences between canvas and Konva
  const safeWidth = maxWidth * 0.85
  const safeHeight = maxHeight * 0.85

  while (fontSize > minFontSize) {
    ctx.font = `${fontSize}px ${fontFamily || 'Arial'}`

    if (wrap) {
      // For wrapped text, check if it fits within height
      const words = text.split(' ')
      let line = ''
      let lineCount = 1
      const lineHeight = fontSize * 1.3 // More generous line height

      for (let word of words) {
        const testLine = line + (line ? ' ' : '') + word
        const metrics = ctx.measureText(testLine)
        // Add letter spacing to width calculation
        const totalWidth = metrics.width + (testLine.length * letterSpacing)
        if (totalWidth > safeWidth && line) {
          line = word
          lineCount++
        } else {
          line = testLine
        }
      }

      const totalHeight = lineCount * lineHeight
      if (totalHeight <= safeHeight) {
        return fontSize
      }
    } else {
      // For non-wrapped text, check width
      const metrics = ctx.measureText(text)
      // Add letter spacing to width calculation
      const totalWidth = metrics.width + (text.length * letterSpacing)
      if (totalWidth <= safeWidth) {
        return fontSize
      }
    }

    fontSize--
  }

  return minFontSize
}

// Determine if field is an image type
export function isImageField(field) {
  const label = (field.label || '').toLowerCase()
  return label.includes('image') || label.includes('picture') || label.includes('pose') || label.includes('qr') || label.includes('logo') || label.includes('overlay')
}

// Determine if field is a QR code type
export function isQrField(field) {
  return (field.label || '').toLowerCase().includes('qr')
}

// Determine if field is a logo type
export function isLogoField(field) {
  return (field.label || '').toLowerCase().includes('logo')
}

// Determine if field is an overlay (uploaded PNG, natural aspect ratio).
export function isOverlayField(field) {
  return (field.label || '').toLowerCase().includes('overlay')
}

// Stacking order on the canvas — mirrors the PDF z-index logic so the editor
// preview matches the rendered output. Lower numbers render first (in back).
//
// Default (non-grad-folio): logo (10) < photo/pose (20) < text (30)
// Grad folio (overlays present): photo/pose (20) < overlay (25) < logo/text (30)
//
// Logo stacking diverges by category because grad folio explicitly wants the
// school logo to sit ABOVE the overlay, whereas other categories let the
// student photo cover the logo when the two regions overlap.
export function getCanvasZIndex(field, { isGradFolio = false } = {}) {
  if (isOverlayField(field)) return 25
  const label = (field.label || '').toLowerCase()
  const isPhoto = label.includes('image') || label.includes('picture') || label.includes('pose') || label.includes('photo')
  if (isLogoField(field)) return isGradFolio ? 30 : 10
  if (isPhoto) return 20
  return 30
}

// Enforce 8x10 or 10x8 aspect ratio for photo fields
// Uses explicit horizontal flag (defaults to portrait)
export function enforcePhotoRatio(width, horizontal = false) {
  if (horizontal) {
    // Landscape: 10:8 — height = width * 8/10
    return { width, height: Math.round(width * 8 / 10) }
  } else {
    // Portrait: 8:10 — height = width * 10/8
    return { width, height: Math.round(width * 10 / 8) }
  }
}

// Generate a unique ID for custom fields
export function generateFieldId() {
  return `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Create a new text field with default properties
export function createTextField(text, x, y, options = {}) {
  return {
    id: generateFieldId(),
    left: x,
    top: y,
    width: options.width || Math.min(300, Math.max(100, text.length * 8)),
    height: options.height || 30,
    text: text,
    sampleText: null,
    textSize: options.textSize || 16,
    color: options.color || '#000000',
    strokeColor: options.strokeColor || '#000000',
    strokeWidth: options.strokeWidth || 0,
    rotation: options.rotation || 0,
    align: options.align || 'left',
    verticalAlign: options.verticalAlign || 'middle',
    spacing: options.spacing || 0,
    wrap: options.wrap || false,
    fontName: options.fontName || 'Arial',
    fontId: options.fontId || null,
    fontStyle: options.fontStyle || 'normal',
    textDecoration: options.textDecoration || '',
    autoFit: options.autoFit || false,
    minFontSize: options.minFontSize || 8,
    label: options.label || 'Custom Text',
    isCustomField: true,
    customerEditable: true, // Default to editable for customer-facing templates
    templatePageId: options.templatePageId || null,
  }
}
